/**
 * Veri katmanı giriş noktası.
 * Tüm kayıtları tek yerden dışa açar ve başlangıçta tutarlılık doğrulaması yapar:
 * bir tarif olmayan bir item'a işaret ediyorsa oyun sessizce bozulmasın, uyarı çıksın.
 */
import { materials, veinMaterials } from './materials';
import { items, itemDef } from './items';
import { recipes } from './recipes';
import { buildPieces } from './build';
import { fishSpecies } from './fish';
import { lootTables } from './loot';
import { biomes } from './biomes';
import { creatures } from './creatures';
import { difficulties } from './difficulty';
import { npcs, questTemplates } from './npcs';
import { bosses } from './bosses';
import { resourceDefs, oreResourceId } from './resources';
import type { ResourceDef } from './resources';

export * from './materials';
export * from './items';
export * from './recipes';
export * from './build';
export * from './fish';
export * from './loot';
export * from './biomes';
export * from './creatures';
export * from './difficulty';
export * from './npcs';
export * from './bosses';
export * from './resources';

/**
 * Maden damarı kaynakları malzemelerden otomatik üretilir.
 * Yeni maden eklendiğinde kazılabilir damarı da kendiliğinden oluşur.
 */
function registerOreResources(): void {
  for (const m of veinMaterials()) {
    const v = m.vein!;
    const def: ResourceDef = {
      id: oreResourceId(m.id),
      name: `${m.name} Damarı`,
      kind: 'ore',
      health: 60 + v.hardness * 45,
      hardness: v.hardness,
      tool: 'pickaxe',
      drops: [
        { item: m.id, min: 1, max: Math.max(1, Math.round(v.clusterMax / 2)), chance: 1 },
        { item: 'stone', min: 1, max: 2, chance: 0.4 },
      ],
      respawn: 0,
      size: 0.9,
      color: m.color,
      radius: 1,
    };
    resourceDefs.register(def);
  }
}
registerOreResources();

export interface ValidationIssue {
  kind: 'error' | 'warn';
  where: string;
  message: string;
}

/** Veri tutarlılığını denetler. Geliştirme sırasında konsola basılır. */
export function validateData(): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const itemExists = (id: string) => items.has(id);

  for (const r of recipes.all()) {
    if (!itemExists(r.output.item)) {
      issues.push({ kind: 'error', where: `recipe:${r.id}`, message: `çıktı item'ı yok: ${r.output.item}` });
    }
    for (const ing of r.ingredients ?? []) {
      if (!itemExists(ing.item)) {
        issues.push({ kind: 'error', where: `recipe:${r.id}`, message: `malzeme yok: ${ing.item}` });
      }
    }
    for (const id of Object.values(r.key ?? {})) {
      if (!itemExists(id)) {
        issues.push({ kind: 'error', where: `recipe:${r.id}`, message: `desen item'ı yok: ${id}` });
      }
    }
  }

  for (const t of lootTables.all()) {
    for (const e of t.entries) {
      if (!itemExists(e.item)) {
        issues.push({ kind: 'error', where: `loot:${t.id}`, message: `item yok: ${e.item}` });
      }
    }
  }

  for (const res of resourceDefs.all()) {
    for (const d of res.drops) {
      if (!itemExists(d.item)) {
        issues.push({ kind: 'error', where: `resource:${res.id}`, message: `düşen item yok: ${d.item}` });
      }
    }
  }

  for (const c of creatures.all()) {
    if (!lootTables.has(c.lootTable)) {
      issues.push({ kind: 'warn', where: `creature:${c.id}`, message: `loot tablosu yok: ${c.lootTable}` });
    }
  }

  for (const b of biomes.all()) {
    for (const s of b.spawns) {
      if (!creatures.has(s.creature)) {
        issues.push({ kind: 'warn', where: `biome:${b.id}`, message: `canlı yok: ${s.creature}` });
      }
    }
    for (const ore of Object.keys(b.oreBias ?? {})) {
      if (!materials.has(ore)) {
        issues.push({ kind: 'warn', where: `biome:${b.id}`, message: `maden yok: ${ore}` });
      }
    }
  }

  for (const boss of bosses.all()) {
    for (const phase of boss.phases) {
      for (const atk of phase.attacks) {
        if (!boss.attacks.some((a) => a.id === atk)) {
          issues.push({ kind: 'error', where: `boss:${boss.id}`, message: `tanımsız saldırı: ${atk}` });
        }
      }
    }
    for (const r of boss.rewards) {
      if (!itemExists(r.item)) {
        issues.push({ kind: 'error', where: `boss:${boss.id}`, message: `ödül item'ı yok: ${r.item}` });
      }
    }
  }

  for (const p of buildPieces.all()) {
    for (const c of p.cost) {
      if (!itemExists(c.item)) {
        issues.push({ kind: 'error', where: `piece:${p.id}`, message: `maliyet item'ı yok: ${c.item}` });
      }
    }
    if (p.upgradeTo && !buildPieces.has(p.upgradeTo)) {
      issues.push({ kind: 'error', where: `piece:${p.id}`, message: `yükseltme hedefi yok: ${p.upgradeTo}` });
    }
  }

  for (const q of questTemplates.all()) {
    for (const t of q.targets) {
      if (t === 'chunk') continue;
      if (!items.has(t) && !creatures.has(t)) {
        issues.push({ kind: 'warn', where: `quest:${q.id}`, message: `hedef bulunamadı: ${t}` });
      }
    }
  }

  return issues;
}

export const registries = {
  materials,
  items,
  recipes,
  buildPieces,
  fishSpecies,
  lootTables,
  biomes,
  creatures,
  difficulties,
  npcs,
  questTemplates,
  bosses,
  resourceDefs,
};

export function dataSummary(): Record<string, number> {
  return Object.fromEntries(Object.entries(registries).map(([k, v]) => [k, v.size]));
}

export { itemDef };
